import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, WifiHigh as Wifi, MapPin, Monitor,
  ShoppingCart, ChartBar, UsersThree, Receipt,
  Envelope, ShieldCheck, Package, Printer, Truck,
  Plus, Minus, UserPlus, Lightning, WhatsappLogo,
} from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO, { softwareJsonLd, buildFaqJsonLd } from '../../components/SEO';

const screenshots = {
  dashboard: '/images/screenshots/dashboard.png',
  pos:       '/images/screenshots/pos.png',
  inventory: '/images/screenshots/inventory.png',
  expenses:  '/images/screenshots/expenses.png',
};

/* ── Scroll reveal ── */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { node.classList.add('revealed'); obs.unobserve(node); } },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);
  return ref;
};
const R = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-on-scroll ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

/* SVG squiggle underline – hero only */
const Squiggle = ({ children }) => (
  <span className="relative inline-block">
    {children}
    <svg
      aria-hidden="true"
      className="absolute left-0 -bottom-1 w-full overflow-visible pointer-events-none"
      viewBox="0 0 100 7"
      preserveAspectRatio="none"
      fill="none"
    >
      <path
        d="M0,5 C15,1 32,7 50,4 C68,1 85,7 100,4"
        stroke="rgba(201,150,12,0.55)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

/* ── Data ── */
const AVATAR_COLORS = ['bg-emerald-600', 'bg-primaryA-500', 'bg-accent-500', 'bg-negative-600'];

const MARQUEE_ITEMS = [
  'Retail Shops', 'Restaurants', 'Salons & Barbers', 'Pharmacies',
  'Mini Marts', 'Electronics Shops', 'Boutiques', 'Bakeries',
  'Hardware Stores', 'Beauty Clinics', 'Supermarkets', 'Service Businesses',
];

const STEPS = [
  {
    num: '01',
    Icon: UserPlus,
    title: 'Sign up in 2 minutes',
    desc: 'Create your account and add your business name. No setup fee, no IT team, no paperwork. Works on any device you already own.',
  },
  {
    num: '02',
    Icon: Package,
    title: 'Add your products & staff',
    desc: 'Upload your inventory, set prices, and create logins for your team. Import from Excel or add products one by one.',
  },
  {
    num: '03',
    Icon: Lightning,
    title: 'Start selling from day one',
    desc: 'Open the POS on any device and ring up your first sale. Data syncs across every branch in real time.',
  },
];

const BENTO = [
  {
    large: true, dark: true,
    Icon: Wifi,
    title: 'Works completely offline',
    desc: "Internet goes down? Your POS stays on. Every transaction is saved locally and syncs automatically when you reconnect. No sale ever lost.",
    badge: 'Zero downtime',
  },
  {
    large: false, dark: false,
    Icon: ShoppingCart,
    title: 'Split Payments',
    desc: 'Cash, mobile money, and card – all in one transaction.',
  },
  {
    large: false, dark: false,
    Icon: MapPin,
    title: 'Multi-Branch',
    desc: 'All your locations, one dashboard.',
  },
  {
    large: false, dark: false,
    Icon: Package,
    title: 'Inventory',
    desc: 'Stock levels, low-stock alerts, and bulk updates.',
  },
  {
    large: false, dark: false,
    Icon: UsersThree,
    title: 'Staff Management',
    desc: 'Roles, access levels, and performance tracking.',
  },
  {
    large: false, dark: false,
    Icon: Receipt,
    title: 'Expense Tracking',
    desc: 'Log and categorize every outgoing penny.',
  },
  {
    large: true, dark: true,
    Icon: ChartBar,
    title: 'Real-time analytics',
    desc: "Revenue, gross profit, top products, staff leaderboard – updated live. A full summary hits your inbox every evening.",
    badge: 'Live data',
  },
];

const TESTIMONIALS = [
  {
    quote: "I cleared GHS 8,400 in my first week. I had no idea my shop was doing that well until SmartPOS showed me the numbers.",
    name: 'Abena K.',
    business: 'Retail shop · Kumasi',
    initial: 'A', color: 'bg-emerald-600',
  },
  {
    quote: "Three branches and I can check stock at all of them from my phone. Before SmartPOS I was driving between locations every day.",
    name: 'Kwame O.',
    business: 'Electronics · Accra',
    initial: 'K', color: 'bg-primaryA-500',
  },
  {
    quote: "My cashier was stealing from me every week. SmartPOS caught it in the first month. Paid for itself immediately.",
    name: 'Akosua M.',
    business: 'Mini mart · Tema',
    initial: 'A', color: 'bg-accent-500',
  },
  {
    quote: "Set up in under an hour. I was ringing up sales the same day I signed up. Nobody else made it this easy.",
    name: 'Emmanuel A.',
    business: 'Salon · Takoradi',
    initial: 'E', color: 'bg-negative-600',
  },
  {
    quote: "The daily email report changed how I run my business. I know my numbers before I even open the shop in the morning.",
    name: 'Gifty A.',
    business: 'Pharmacy · Accra',
    initial: 'G', color: 'bg-emerald-700',
  },
  {
    quote: "My customers love that I can split payment between cash and mobile money. Nobody else in my area does that.",
    name: 'Samuel T.',
    business: 'Restaurant · Kumasi',
    initial: 'S', color: 'bg-primaryA-600',
  },
];

const FAQS = [
  {
    q: 'Does it work without internet?',
    a: "Yes. SmartPOS works fully offline. You can keep selling even when your connection drops and everything syncs automatically once you are back online. No sales are ever lost.",
  },
  {
    q: 'Can my staff use it on their phones?',
    a: "Yes. SmartPOS runs in any browser on any device: phone, tablet, or laptop. No app download needed. Each staff member gets their own login with the right access level.",
  },
  {
    q: 'How many staff accounts can I add?',
    a: "Unlimited. There is no extra charge per user. Add as many cashiers, managers, and attendants as you need. All included in your plan.",
  },
  {
    q: 'Can I manage more than one branch?',
    a: "Yes. Multi-branch support is built in. See stock levels, sales, and staff performance for every location from one dashboard.",
  },
  {
    q: 'What happens when my free trial ends?',
    a: "You pick a plan and continue. All your data including products, customers, and sales history stays exactly as it is. Nothing is deleted.",
  },
  {
    q: 'Is my business data safe?',
    a: "Yes. Your data is encrypted and backed up automatically. You can also export everything to a spreadsheet at any time.",
  },
];

/* ── Component ── */
const HomePage = () => {
  const [activeTab, setActiveTab]   = useState('pos');
  const [openFaq, setOpenFaq]       = useState(null);
  const tabRef                      = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({});

  const productTabs = [
    {
      id: 'pos', label: 'Point of Sale', screenshot: screenshots.pos,
      headline: 'Ring up sales in seconds',
      description: 'Scan a barcode or search by name. Apply discounts, split the payment across cash and mobile money, print a receipt – without slowing down.',
      details: ['Barcode scanning', 'Split payments', 'Hold & resume', 'Print receipts'],
    },
    {
      id: 'dashboard', label: 'Dashboard', screenshot: screenshots.dashboard,
      headline: 'Know your numbers instantly',
      description: 'Revenue, top products, staff performance, branch comparisons – updated in real time. A daily summary lands in your inbox every night.',
      details: ['Revenue tracking', 'Staff performance', 'Daily email reports', 'CSV export'],
    },
    {
      id: 'inventory', label: 'Inventory', screenshot: screenshots.inventory,
      headline: 'Never run out of what sells',
      description: 'See stock levels across all branches, get alerts when items run low, and update quantities in bulk.',
      details: ['Stock levels', 'Low stock alerts', 'Batch updates', 'Categories'],
    },
    {
      id: 'expenses', label: 'Expenses', screenshot: screenshots.expenses,
      headline: 'Track every outgoing penny',
      description: 'Log expenses, categorize spending, and see exactly where your money goes. Filter by date, staff, or category.',
      details: ['Categorize spending', 'Cash flow view', 'Date filtering', 'Staff logging'],
    },
  ];

  useEffect(() => {
    const updateIndicator = () => {
      const container = tabRef.current;
      if (!container) return;
      const activeBtn = container.querySelector(`[data-tab="${activeTab}"]`);
      if (activeBtn) {
        setIndicatorStyle({ left: activeBtn.offsetLeft, width: activeBtn.offsetWidth });
      }
    };
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeTab]);

  const activeProduct = productTabs.find(t => t.id === activeTab);

  const faqJsonLd = buildFaqJsonLd(FAQS.map(({ q, a }) => ({ question: q, answer: a })));

  return (
    <div className="min-h-screen bg-primaryB-50 overflow-x-hidden">
      <SEO
        title="Home"
        description="Smart POS Africa – All-in-one POS software for retail, restaurants and service businesses. Point of sale, inventory, staff management, expenses, real-time analytics. Works offline across Ghana, Nigeria and beyond."
        path="/"
        keywords="POS software Africa, pos software Ghana, pos software Nigeria, point of sale Africa, pos africa, software solutions africa, retail POS africa, restaurant POS africa, shop management system Africa, business software Ghana, business software Nigeria"
        jsonLd={[softwareJsonLd, faqJsonLd]}
      />
      <LandingNav />

      {/* ━━━ HERO ━━━ */}
      <section className="pt-24 sm:pt-32 lg:pt-44 pb-12 sm:pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <div className="lg:col-span-5 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-emerald-500/[0.08] border border-emerald-500/[0.12] rounded-full px-4 py-1.5 mb-6">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                <span className="text-emerald-800 text-xs font-bold tracking-wide">1 month free trial</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] xl:text-6xl font-extrabold text-primaryB-900 leading-[1.08] tracking-tight mb-2">
                Sell more,{' '}
                <Squiggle>stress less</Squiggle>.
              </h1>

              <p className="text-emerald-700 text-sm font-semibold mb-5">
                POS software built for African businesses – retail, restaurants &amp; services.
              </p>

              <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
                Whether you sell in-store, online, or both – one app for your POS, inventory, staff, expenses, and customers. Takes 5 minutes.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-7 sm:justify-center lg:justify-start">
                <Link
                  to="/signup"
                  className="group inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-3.5 rounded-full font-bold transition-all duration-200"
                >
                  Start for free
                  <ArrowRight weight="bold" className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/features"
                  className="inline-flex items-center justify-center gap-2 text-primaryB-500 hover:text-primaryB-900 px-7 py-3.5 rounded-full font-semibold transition-all duration-200"
                >
                  See how it works
                </Link>
              </div>

              {/* Social proof row */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-3 justify-center lg:justify-start">
                <div className="flex items-center gap-2.5">
                  <div className="flex -space-x-2">
                    {['A', 'K', 'E', 'G'].map((initial, i) => (
                      <div key={i} className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-primaryB-50 ${AVATAR_COLORS[i]}`}>
                        {initial}
                      </div>
                    ))}
                  </div>
                  <p className="text-primaryB-400 text-sm">
                    <span className="font-bold text-primaryB-700">50+</span> businesses
                  </p>
                </div>
                <span className="hidden sm:block w-px h-4 bg-primaryB-200" />
                <div className="flex items-center gap-1.5 text-primaryB-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  No credit card
                </div>
                <div className="flex items-center gap-1.5 text-primaryB-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Works offline
                </div>
              </div>

              {/* Support nudge */}
              <p className="mt-4 text-xs text-primaryB-300 text-center lg:text-left">
                Have questions?{' '}
                <a
                  href="https://wa.me/233559306229"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 font-semibold hover:underline inline-flex items-center gap-1"
                >
                  <WhatsappLogo weight="fill" className="w-3.5 h-3.5" />
                  Chat with us on WhatsApp
                </a>
              </p>
            </div>

            {/* Screenshots – desktop with phone overlapping */}
            <div className="lg:col-span-7">
              <div className="relative">
                {/* Subtle background shape */}
                <div className="absolute -top-6 -right-6 -bottom-6 -left-6 bg-emerald-800/[0.03] rounded-[2rem] -rotate-1 hidden lg:block" />

                {/* Desktop screenshot – full width, phone will overlap on top */}
                <div className="relative z-10 bg-primaryB-50 rounded-3xl p-1.5 shadow-elevated border border-primaryB-200/60">
                  <img src={screenshots.dashboard} alt="SmartPOS Dashboard" className="w-full rounded-xl" loading="eager" />
                </div>

                {/* Phone – overlaps desktop from the right, slightly rotated */}
                <div className="absolute z-20 -right-6 sm:-right-8 bottom-0 w-28 sm:w-32 lg:w-40 landing-float-1" style={{ transform: 'rotate(3deg)' }}>
                  <img
                    src="/images/screenshots/hero-mobile.png"
                    alt="SmartPOS on mobile"
                    className="w-full drop-shadow-2xl"
                    loading="eager"
                  />
                </div>

                {/* Floating card – bottom left */}
                <div className="absolute z-30 -bottom-3 left-2 sm:-bottom-4 sm:-left-4 landing-float-2 hidden sm:block bg-white rounded-2xl shadow-card border border-primaryB-200/60 px-4 py-3">
                  <p className="text-primaryB-900 text-sm font-bold">GHS 4,331</p>
                  <p className="text-primaryB-300 text-[11px]">Today's revenue</p>
                </div>

                {/* Floating card – top right of desktop */}
                <div className="absolute z-30 -top-3 right-20 sm:-top-4 landing-float-3 hidden sm:block bg-white rounded-2xl shadow-card border border-primaryB-200/60 px-4 py-2.5">
                  <p className="text-primaryB-900 text-xs font-bold">Daily report sent</p>
                  <p className="text-primaryB-300 text-[10px]">8:00 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ STATS ━━━ */}
      <section className="border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-primaryB-200/60">
            {[
              { value: '5 min', label: 'Setup time' },
              { value: 'GHS 0', label: 'Upfront cost' },
              { value: '24/7', label: 'Access anywhere' },
            ].map((stat) => (
              <div key={stat.label} className="py-6 sm:py-10 text-center px-2">
                <div className="text-lg sm:text-3xl font-extrabold text-primaryB-900 mb-0.5 sm:mb-1">{stat.value}</div>
                <div className="text-xs sm:text-sm text-primaryB-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ MARQUEE ━━━ */}
      <section className="py-7 sm:py-10 border-b border-primaryB-200/60 overflow-hidden">
        <div className="flex items-center gap-6 mb-4 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <span className="text-primaryB-300 text-xs font-bold tracking-widest uppercase shrink-0">Works for</span>
        </div>
        <div
          className="overflow-hidden"
          style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}
        >
          <div className="marquee-track">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span key={i} className="inline-flex items-center gap-3 px-6 text-sm font-semibold text-primaryB-500 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-primaryA-400 shrink-0" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ HOW IT WORKS ━━━ */}
      <section className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3 text-center">How it works</p>
            <h2 className="text-center text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight leading-tight mb-4">
              Up and running in{' '}
              <span className="italic text-emerald-600">three steps</span>.
            </h2>
            <p className="text-center text-primaryB-400 text-base sm:text-lg mb-16 sm:mb-20 max-w-md mx-auto">
              No IT setup. No hardware to buy. No training required.
            </p>
          </R>

          <div className="relative">
            {/* Horizontal connecting line through circle centers – sm+ only */}
            <div className="hidden sm:block absolute left-0 right-0 top-7 h-px bg-primaryB-200/80 z-0" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8 lg:gap-12">
              {STEPS.map(({ num, Icon, title, desc }, i) => (
                <R key={num} delay={i * 100}>
                  <div className="flex flex-col items-center text-center">
                    {/* Circle icon – z-10 floats above the connecting line, ring creates gap */}
                    <div className="relative z-10 w-14 h-14 rounded-full bg-primaryB-900 flex items-center justify-center shadow-card mb-5 ring-4 ring-primaryB-50">
                      <Icon weight="bold" className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-primaryB-300 text-xs font-bold tracking-widest uppercase mb-2">{num}</span>
                    <h3 className="font-extrabold text-primaryB-900 text-base sm:text-lg mb-2">{title}</h3>
                    <p className="text-primaryB-500 text-sm leading-relaxed">{desc}</p>
                  </div>
                </R>
              ))}
            </div>
          </div>

          <R delay={320}>
            <div className="text-center mt-14">
              <Link
                to="/signup"
                className="group inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-3.5 rounded-full font-bold transition-all duration-200 text-sm"
              >
                Your business could be live in 5 minutes
                <ArrowRight weight="bold" className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </R>
        </div>
      </section>

      {/* ━━━ TABBED PRODUCT SHOWCASE ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28 bg-primaryB-50 border-y border-primaryB-200/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <h2 className="text-2xl sm:text-3xl lg:text-[2.75rem] font-extrabold text-primaryB-900 tracking-tight leading-tight max-w-lg mb-8 sm:mb-10">
              See what you're getting.
            </h2>
          </R>

          <R delay={60}>
            <div className="grid grid-cols-2 gap-2 mb-8 sm:hidden">
              {productTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 ${
                    activeTab === tab.id ? 'bg-primaryB-900 text-white shadow-card' : 'bg-primaryB-100 text-primaryB-400'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mb-10 hidden sm:block" ref={tabRef}>
              <div className="relative flex gap-1 bg-primaryB-100 rounded-full p-1 w-fit">
                <div
                  className="absolute top-1 h-[calc(100%-8px)] bg-primaryB-50 rounded-full shadow-card transition-all duration-300 ease-out"
                  style={indicatorStyle}
                />
                {productTabs.map((tab) => (
                  <button
                    key={tab.id}
                    data-tab={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative z-10 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id ? 'text-primaryB-900' : 'text-primaryB-400 hover:text-primaryB-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </R>

          <div className="grid lg:grid-cols-5 gap-6 sm:gap-8 lg:gap-14 items-start">
            <div className="lg:col-span-3 order-2 lg:order-1">
              <div className="bg-primaryB-50 rounded-3xl p-1.5 sm:p-2 shadow-elevated border border-primaryB-200/60 overflow-hidden">
                <div className="relative">
                  {productTabs.map((tab) => (
                    <img
                      key={tab.id}
                      src={tab.screenshot}
                      alt={tab.label}
                      className={`w-full rounded-xl transition-all duration-500 ease-out ${
                        activeTab === tab.id ? 'opacity-100 relative' : 'opacity-0 absolute inset-0 pointer-events-none'
                      }`}
                      loading={tab.id === 'pos' ? 'eager' : 'lazy'}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 lg:pt-2 order-1 lg:order-2">
              <div key={activeTab} className="landing-tab-content">
                <h3 className="text-xl sm:text-2xl font-extrabold text-primaryB-900 tracking-tight mb-2 sm:mb-3">
                  {activeProduct.headline}
                </h3>
                <p className="text-primaryB-500 text-sm sm:text-base leading-relaxed mb-5 sm:mb-6">
                  {activeProduct.description}
                </p>
                <ul className="space-y-2 sm:space-y-2.5">
                  {activeProduct.details.map((d) => (
                    <li key={d} className="text-primaryB-700 text-sm flex items-center gap-3">
                      <span className="w-1 h-1 rounded-full bg-primaryB-300 flex-shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ FEATURE BENTO GRID ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">Features</p>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-10 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight leading-tight">
                Everything you need{' '}
                <span className="italic text-emerald-600">to grow</span>.
              </h2>
              <Link
                to="/features"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold text-sm transition-all duration-200 shrink-0"
              >
                All features <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
            </div>
          </R>

          {/* Bento grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {/* Row 1: large dark + small */}
            {/* Large dark card – Works Offline */}
            <R delay={0} className="sm:col-span-2">
              <div className="relative overflow-hidden bg-primaryB-900 rounded-2xl p-6 sm:p-8 h-full min-h-[200px]">
                <div className="absolute bottom-0 right-0 opacity-[0.04] pointer-events-none select-none">
                  <Wifi weight="fill" className="w-48 h-48 text-white" />
                </div>
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-500/20 rounded-full px-3 py-1 mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-emerald-400 text-xs font-bold">Zero downtime</span>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                    <Wifi weight="bold" className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-extrabold text-white text-lg sm:text-xl mb-2">Works completely offline</p>
                  <p className="text-white/50 text-sm leading-relaxed max-w-sm">
                    Internet goes down? Your POS stays on. Every transaction is saved locally and syncs automatically when you reconnect. No sale ever lost.
                  </p>
                </div>
              </div>
            </R>

            {/* Small card – Split Payments */}
            <R delay={60} className="h-full">
              <div className="bg-white border border-primaryB-200/60 rounded-2xl p-5 sm:p-6 hover:border-primaryB-300/80 hover:shadow-card transition-all duration-200 h-full">
                <div className="w-9 h-9 rounded-xl bg-primaryB-100 flex items-center justify-center mb-4">
                  <ShoppingCart weight="bold" className="w-4 h-4 text-primaryB-600" />
                </div>
                <p className="font-bold text-primaryB-900 text-sm mb-1.5">Split Payments</p>
                <p className="text-primaryB-400 text-xs sm:text-sm leading-relaxed">Cash, mobile money, and card – all in one transaction.</p>
              </div>
            </R>

            {/* Row 2: 3 small cards */}
            <R delay={80}>
              <div className="bg-white border border-primaryB-200/60 rounded-2xl p-5 sm:p-6 hover:border-primaryB-300/80 hover:shadow-card transition-all duration-200">
                <div className="w-9 h-9 rounded-xl bg-primaryB-100 flex items-center justify-center mb-4">
                  <MapPin weight="bold" className="w-4 h-4 text-primaryB-600" />
                </div>
                <p className="font-bold text-primaryB-900 text-sm mb-1.5">Multi-Branch</p>
                <p className="text-primaryB-400 text-xs sm:text-sm leading-relaxed">All your locations, one dashboard.</p>
              </div>
            </R>

            <R delay={120}>
              <div className="bg-white border border-primaryB-200/60 rounded-2xl p-5 sm:p-6 hover:border-primaryB-300/80 hover:shadow-card transition-all duration-200">
                <div className="w-9 h-9 rounded-xl bg-primaryB-100 flex items-center justify-center mb-4">
                  <Package weight="bold" className="w-4 h-4 text-primaryB-600" />
                </div>
                <p className="font-bold text-primaryB-900 text-sm mb-1.5">Inventory</p>
                <p className="text-primaryB-400 text-xs sm:text-sm leading-relaxed">Stock levels, low-stock alerts, and bulk updates.</p>
              </div>
            </R>

            <R delay={160}>
              <div className="bg-white border border-primaryB-200/60 rounded-2xl p-5 sm:p-6 hover:border-primaryB-300/80 hover:shadow-card transition-all duration-200">
                <div className="w-9 h-9 rounded-xl bg-primaryB-100 flex items-center justify-center mb-4">
                  <UsersThree weight="bold" className="w-4 h-4 text-primaryB-600" />
                </div>
                <p className="font-bold text-primaryB-900 text-sm mb-1.5">Staff Management</p>
                <p className="text-primaryB-400 text-xs sm:text-sm leading-relaxed">Roles, access levels, and performance tracking.</p>
              </div>
            </R>

            {/* Row 3: small + large dark */}
            <R delay={100} className="h-full">
              <div className="bg-white border border-primaryB-200/60 rounded-2xl p-5 sm:p-6 hover:border-primaryB-300/80 hover:shadow-card transition-all duration-200 h-full flex flex-col">
                <div className="w-9 h-9 rounded-xl bg-primaryB-100 flex items-center justify-center mb-3">
                  <Receipt weight="bold" className="w-4 h-4 text-primaryB-600" />
                </div>
                <p className="font-bold text-primaryB-900 text-sm mb-0.5">Expense Tracking</p>
                <p className="text-primaryB-400 text-xs leading-relaxed mb-4">Log and categorize every outgoing penny.</p>
                {/* Mini expense breakdown */}
                <div className="mt-auto space-y-2">
                  {[
                    { label: 'Supplies', amount: '₵840', color: 'bg-primaryA-400' },
                    { label: 'Utilities', amount: '₵320', color: 'bg-accent-400' },
                    { label: 'Logistics', amount: '₵195', color: 'bg-positive-500' },
                  ].map(({ label, amount, color }) => (
                    <div key={label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${color} flex-shrink-0`} />
                        <span className="text-xs text-primaryB-500">{label}</span>
                      </div>
                      <span className="text-xs font-semibold text-primaryB-700">{amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            </R>

            {/* Large dark card – Analytics */}
            <R delay={140} className="sm:col-span-2">
              <div className="relative overflow-hidden bg-primaryB-900 rounded-2xl p-6 sm:p-8 h-full min-h-[200px]">
                <div className="absolute bottom-0 right-0 opacity-[0.04] pointer-events-none select-none">
                  <ChartBar weight="fill" className="w-48 h-48 text-white" />
                </div>
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-1.5 bg-primaryA-500/20 border border-primaryA-500/20 rounded-full px-3 py-1 mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-primaryA-400 animate-pulse" />
                    <span className="text-primaryA-300 text-xs font-bold">Live data</span>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                    <ChartBar weight="bold" className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-extrabold text-white text-lg sm:text-xl mb-2">Real-time analytics</p>
                  <p className="text-white/50 text-sm leading-relaxed max-w-sm">
                    Revenue, gross profit, top products, staff leaderboard – updated live. A full summary hits your inbox every evening.
                  </p>
                </div>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* ━━━ TESTIMONIALS ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28 bg-primaryB-900">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-white/30 text-xs font-bold tracking-widest uppercase mb-3">Merchant Stories</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight mb-12 sm:mb-16">
              Real businesses,{' '}
              <span className="italic text-emerald-400">real results.</span>
            </h2>
          </R>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {TESTIMONIALS.map(({ quote, name, business, initial, color }, i) => (
              <R key={name} delay={Math.floor(i / 3) * 80}>
                <div className="bg-primaryB-800/60 border border-white/[0.06] rounded-2xl p-5 sm:p-6 flex flex-col gap-5 h-full">
                  <p className="text-white/75 text-sm leading-relaxed flex-1">"{quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${color}`}>
                      {initial}
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold leading-tight">{name}</p>
                      <p className="text-white/40 text-xs">{business}</p>
                    </div>
                  </div>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ PRICING ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28 pricing-dots border-y border-primaryB-200/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            <R>
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight leading-tight mb-4">
                  One plan, one price.
                </h2>
                <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                  No tiers. No features locked behind upgrades. Every business gets access to everything from day one.
                </p>
                <ul className="space-y-2.5">
                  {['Unlimited staff accounts', 'All features from day one', 'Daily email reports', 'Works offline', 'Multi-branch support', 'Delivery management'].map((b) => (
                    <li key={b} className="flex items-center gap-3 text-primaryB-700 text-sm">
                      <span className="w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </R>

            <R delay={100}>
              <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-10 border border-primaryB-200/60 shadow-card max-w-md mx-auto lg:max-w-none lg:mx-0">
                <p className="text-primaryB-300 text-xs sm:text-sm font-medium tracking-wider uppercase mb-4 sm:mb-5">Business Plan</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-primaryB-900">GHS 100</span>
                  <span className="text-primaryB-300 font-medium text-base sm:text-lg">/mo</span>
                </div>
                <p className="text-primaryB-300 text-xs sm:text-sm mb-6 sm:mb-8">or GHS 1,000/year – save 17%</p>
                <Link
                  to="/signup"
                  className="group flex items-center justify-center gap-2 w-full bg-primaryB-900 hover:bg-primaryB-800 text-white py-3.5 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-200"
                >
                  Start free trial
                  <ArrowRight weight="bold" className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <p className="text-center text-primaryB-300 text-xs mt-3 sm:mt-4">Free for 1 month · No credit card</p>
                <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-primaryB-200/60">
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold text-sm transition-all duration-200"
                  >
                    See full pricing details <ArrowRight weight="bold" className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* ━━━ FAQ ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">FAQ</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 tracking-tight mb-2">
              Questions we{' '}
              <span className="italic text-emerald-600">always get</span>.
            </h2>
            <p className="text-primaryB-400 text-base mb-10 sm:mb-12">
              Still unsure? These are the things most business owners ask before signing up.
            </p>
          </R>

          <div className="space-y-3">
            {FAQS.map(({ q, a }, i) => {
              const isOpen = openFaq === i;
              return (
                <R key={q} delay={i * 40}>
                  <div className={`border rounded-2xl overflow-hidden transition-all duration-200 ${isOpen ? 'border-primaryB-300/80 shadow-card' : 'border-primaryB-200/60 bg-white'}`}>
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5 text-left"
                    >
                      <span className={`font-semibold text-sm sm:text-base transition-colors duration-150 ${isOpen ? 'text-primaryB-900' : 'text-primaryB-700'}`}>
                        {q}
                      </span>
                      <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-200 ${isOpen ? 'bg-primaryB-900' : 'bg-primaryB-100'}`}>
                        {isOpen
                          ? <Minus weight="bold" className="w-3 h-3 text-white" />
                          : <Plus  weight="bold" className="w-3 h-3 text-primaryB-500" />
                        }
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 sm:px-6 sm:pb-6 -mt-1">
                        <p className="text-primaryB-500 text-sm leading-relaxed">{a}</p>
                      </div>
                    )}
                  </div>
                </R>
              );
            })}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default HomePage;
